import { CONTENT_STREAM_PART_PARTITION, isSabUsable, LOW_CONTENT_STREAM_PART_PARTITION, PEER_DIAG_PLUGIN_ID, Settings, SPEAKER_STREAM_PART_PARTITION, UserRole, } from '@streamr/tv-engine';
import { areEqualBinaries, EcdsaSecp256r1, hexToBinary, toUserId, toUserIdRaw, } from '@streamr/utils';
import { Mutex } from 'async-mutex';
import { proxy, transfer, wrap } from 'comlink';
import { EventEmitter } from 'eventemitter3';
import Worker from 'web-worker';
import { canonicalUserId } from '../utils/canonicalUserId';
import { drainPeerDiagLines, getPeerDiagQueueState, isPeerDiagRequested, isPeerDiagSinkEnabled, } from '../utils/peerDiagnosticsPage';
import { getRemoteLogPageConfig } from '../utils/remoteLogger';
import { createWorker } from '../utils/workerUtils';
import { GAP_DIAGNOSTICS_MASTER_ENABLE } from './streamrapp/GapDiagnostics';
import { readLowQualityStreamOptions, } from './streamrapp/lowQualityStreamOptions';
import { MediaManager } from './streamrapp/MediaManager';
import { readMediaChannelOptions } from './streamrapp/mediaChannelOptions';
import { readMediaSendQueueMaxAgeMs } from './streamrapp/mediaSendQueueOptions';
import { GUEST_EXISTING_PART_REJOIN, GUEST_ONE_TO_ONE_REJOIN, HOST_ONE_TO_ONE_REJOIN, OneToOneRejoinPolicy, } from './streamrapp/OneToOneRejoinPolicy';
import { PeerDiagnosticsManager, PeerDiagnosticsSink, } from './streamrapp/PeerDiagnosticsManager';
import { perfWorkerName } from './streamrapp/perfFlags';
import { RecordingCaptureManager } from './streamrapp/recording/RecordingCaptureManager';
import { readRecordingUploadOptions } from './streamrapp/recording/recordingUploadOptions';
import { StreamrWrapperProxy } from './streamrapp/StreamrWrapperProxy';
import { Timekeeper } from './streamrapp/Timekeeper';
import { TurnCredentialManager } from './streamrapp/TurnCredentialManager';
import { readUplinkCcOptions } from './streamrapp/uplinkCcOptions';
import { readViewerQualityEnvironment, resolveViewerQualityOptions, } from './streamrapp/viewerQualityOptions';
/**
 * Yield to the macrotask queue so the message-loop can drain MessagePort
 * events (e.g. the AudioWorklet's `played` notifications) between heavy
 * awaited steps. Without this, a sequence of `await`s on already-resolved
 * promises stays in the microtask queue and the main thread does not service
 * MessagePort messages until the entire sequence finishes — which can produce
 * stalls of one or more seconds during role transitions on iPhone Safari.
 */
function yieldToEventLoop() {
    return new Promise((resolve) => setTimeout(resolve, 0));
}
function parseOneToOneStreamUrl(url) {
    const hashIndex = url.indexOf('#');
    if (hashIndex === -1) {
        throw new Error(`Invalid oneToOneStreamUrl: no # separator in "${url}"`);
    }
    const streamId = url.substring(0, hashIndex);
    const aesKeyHex = url.substring(hashIndex + 1);
    return { streamId, aesKey: hexToBinary(aesKeyHex) };
}
/** a picture of the composite for a recording's listing entry */
const RECORDING_THUMBNAIL_ATTEMPTS = 3;
const RECORDING_THUMBNAIL_TIMEOUT_MS = 10_000;
/** how often a one-to-one watch reads the part's state */
const ONE_TO_ONE_WATCH_TICK_MS = 1000;
/** an inbound packet this recent means the other side's media reaches us */
const ONE_TO_ONE_INBOUND_FRESH_MS = 3000;
export class StreamrAppApi extends EventEmitter {
    streamrWrapper;
    networkingWorkerRaw;
    networkingWorkerProxy;
    turnCredentialManager;
    peerDiagManager;
    peerDiagSink;
    mediaManager;
    mutex;
    heartbeatTimeoutRef;
    heartbeatIntervalRef;
    streamId;
    broadcasterUserId;
    aesKey;
    ownUserId;
    displayName;
    lastLocalConnectionSummary;
    userManager;
    userRole;
    acceptedParticipantType = 0;
    oneToOneStreamsByUser = new Map();
    oneToOneInboundAudioSeen = false;
    ownOneToOneJoinedAtMs;
    lowContentPartitionJoined = false;
    ownOneToOneRejoinEpoch = 0;
    ownOneToOneStreamId;
    pendingN1GuestIds = new Set();
    n1StartInFlight = new Set();
    n1PendingReasons = new Map();
    broadcasterPrivateKey;
    /** the recording that is being captured, if any */
    activeRecordingId;
    recordingStates = new Map();
    constructor(opts) {
        super();
        this.streamId = opts.streamId;
        this.broadcasterUserId = opts.broadcasterUserId;
        this.aesKey = opts.aesKey;
        this.userRole = opts.initialUserRole ?? UserRole.VIEWER;
        this.mediaManager = new MediaManager();
        this.mutex = new Mutex();
    }
    async start(broadcasterPrivateKey) {
        await this.mutex.acquire();
        try {
            this.broadcasterPrivateKey = broadcasterPrivateKey;
            this.networkingWorkerRaw = this.createNetworkingWorkerInstance();
            this.networkingWorkerProxy = wrap(this.networkingWorkerRaw);
            // Allocate the shared Timekeeper cell and bind it on both sides
            // (main + NetworkingWorker). After this, per-frame timestamp
            // writes from the NetworkingWorker hot path no longer need to
            // hop through the main thread.
            //
            // Requires `crossOriginIsolated` — without it the worker cannot
            // accept a `SharedArrayBuffer` over `postMessage`. The
            // `SharedArrayBuffer` constructor itself can be enabled by
            // Chromium dev flags without COI, so a simple `typeof` check is
            // not sufficient.
            if (isSabUsable()) {
                const timekeeperSab = new SharedArrayBuffer(Timekeeper.SAB_BYTES);
                Timekeeper.bindSharedSab(timekeeperSab);
                await this.networkingWorkerProxy.bindTimekeeperSab(timekeeperSab);
            }
            else {
                console.warn('[StreamrAppApi] SharedArrayBuffer/COI unavailable — Timekeeper updates will route through main');
            }
            await this.networkingWorkerProxy.setEventCallback(proxy(this.onNetworkingWorkerEvent));
            // Install the WebRTC bridge on the main thread. This sends a
            // bridge port to the NetworkingWorker via raw postMessage.
            // The worker intercepts this before Comlink and stores it for
            // forwarding to StreamrWrapper's internal NetworkNode worker.
            // biome-ignore lint/correctness/noUndeclaredDependencies: @streamr/dht is a file: dependency
            const { installWebrtcBridge } = await import('@streamr/dht');
            installWebrtcBridge(this.networkingWorkerRaw);
            const remoteLogPageConfig = getRemoteLogPageConfig();
            const remoteLog = remoteLogPageConfig
                ? { ...remoteLogPageConfig, source: 'iPhone:networkingWorker' }
                : null;
            // TURN: start the node with STUN + any cached credential, then
            // mint a fresh one over the node itself (the credential RPC needs
            // a running transport) and push it via setIceServers.
            const workerProxy = this.networkingWorkerProxy;
            const turnKey = broadcasterPrivateKey ??
                new EcdsaSecp256r1().generateKeyPair().privateKey;
            this.turnCredentialManager = new TurnCredentialManager({
                getTurnCredentials: (request, target, timeoutMs) => workerProxy.getTurnCredentials(request, target, timeoutMs),
                setIceServers: (iceServers) => workerProxy.setIceServers(iceServers),
            }, turnKey, this.streamId);
            const uplinkCc = readUplinkCcOptions();
            console.log('[uplink-cc]', JSON.stringify({ ev: 'options', ...uplinkCc }));
            const mediaChannel = readMediaChannelOptions();
            console.log('[media-channel]', JSON.stringify(mediaChannel));
            await this.networkingWorkerProxy.start(this.streamId, {
                aesKey: this.aesKey,
                // Gap diagnostics require an explicit `?gapDiagnostics=true`
                // — no longer implied by a remoteLog endpoint, so the CPU
                // measurement harness can ship logs without the per-frame
                // probe overhead biasing what it measures.
                diagnosticsEnabled: this.isGapDiagnosticsEnabled(),
                myPrivateKey: broadcasterPrivateKey,
                skipContentPartition: true,
                remoteLog,
                iceServers: this.turnCredentialManager.getInitialIceServers(),
                uplinkCc,
                viewerQuality: { tune: this.viewerQualityTune },
                mediaSendQueueMaxAgeMs: readMediaSendQueueMaxAgeMs(),
                mediaChannel,
            });
            // fire-and-forget: never delays session start; logs on failure
            void this.turnCredentialManager.start();
            this.startPeerDiagnostics();
            // Initialize MediaManager with the NetworkingWorker
            await this.mediaManager.init(this.networkingWorkerProxy);
            // Start workers for the initial role, then wire their ports
            // to the NetworkingWorker
            await this.startWorkersForRole(this.userRole);
            await this.mediaManager.wireNetworkingPorts({
                skipIndividualPorts: this.shouldSkipIndividualPorts(),
            });
            this.mediaManager.setPerTrackAudioEncodingEnabled(!this.shouldSkipIndividualPorts());
            this.mediaManager.setPerTrackVideoEncodingEnabled(!this.shouldSkipIndividualPorts());
            if (this.roleUsesSpeakerPartition(this.userRole)) {
                await this.activateSpeakerPartition();
            }
            this.syncSpeakerPartitionPublishing();
            this.ownUserId = toUserIdRaw((await this.networkingWorkerProxy.getMyUserId()));
            this.streamrWrapper = new StreamrWrapperProxy(this.networkingWorkerProxy, toUserId(this.ownUserId));
            this.emit('streamrWrapperReady', this.streamrWrapper);
            console.log('StreamrAppApi::start() ownUserId', toUserId(this.ownUserId), 'broadcasterUserId', toUserId(this.broadcasterUserId), 'userRole', this.userRole);
            this.setupHeartbeat();
        }
        finally {
            this.mutex.release();
        }
    }
    /**
     * Peer diagnostics (`?peerDiag=1` reports, `?gapDiagnostics=true` or
     * `?peerDiagSink=1` prints what others report): see PeerDiagnosticsManager.
     */
    startPeerDiagnostics() {
        const proxy = this.networkingWorkerProxy;
        if (!proxy)
            return;
        const publish = async (data) => {
            await proxy.publishPluginMessage({
                pluginId: PEER_DIAG_PLUGIN_ID,
                data,
            });
        };
        if (isPeerDiagRequested() && !this.peerDiagManager) {
            this.peerDiagManager = new PeerDiagnosticsManager({
                publish,
                drain: drainPeerDiagLines,
                queueState: getPeerDiagQueueState,
            });
            this.peerDiagManager.start();
            console.log(`[diag-tap] reporting as ${this.peerDiagManager.getRid()}`);
        }
        if ((isPeerDiagSinkEnabled() || this.isGapDiagnosticsEnabled()) &&
            !this.peerDiagSink) {
            this.peerDiagSink = new PeerDiagnosticsSink({
                publish,
                log: (line) => console.log(line),
            });
            this.peerDiagSink.start();
        }
    }
    async stop() {
        await this.mutex.acquire();
        try {
            this.peerDiagManager?.stop();
            this.peerDiagSink?.stop();
            this.clearHeartbeatTimers();
            window.removeEventListener('beforeunload', this.onBeforeUnload);
            this.sendLeave();
            await this.deactivateContentPartition();
            await this.deactivateLowContentPartition();
            await this.deactivateSpeakerPartition();
            // capture ends with the session; the upload goes on in the
            // recording worker, which belongs to the page
            await this.stopRecording().catch(console.error);
            await this.leaveAllOneToOneStreams();
            this.clearAllPendingN1();
            this.turnCredentialManager?.stop();
            this.turnCredentialManager = undefined;
            await this.mediaManager.stop();
            if (this.ownUserId &&
                areEqualBinaries(this.ownUserId, this.broadcasterUserId)) {
                await this.mediaManager.stopPublishingViewerStream();
            }
            await this.networkingWorkerProxy?.stop();
            this.networkingWorkerRaw?.terminate();
            this.networkingWorkerProxy = undefined;
            this.networkingWorkerRaw = undefined;
        }
        finally {
            this.mutex.release();
        }
    }
    getMediaManager() {
        return this.mediaManager;
    }
    // --- Recording ---
    /**
     * Starts recording the composite this node publishes. What is stored
     * is the published packets themselves, so recording costs the host no
     * encoding and no encryption of its own. Capture starts at once;
     * uploading happens in the background and continues after
     * stopRecording(), and after this session has ended.
     */
    async startRecording(options) {
        if (!this.networkingWorkerProxy || !this.broadcasterPrivateKey) {
            throw new Error('only the broadcaster of a session can record');
        }
        if (!this.mediaManager.isViewerCompositeActive()) {
            throw new Error('there is no composite to record');
        }
        if (this.activeRecordingId !== undefined) {
            throw new Error('a recording is already running');
        }
        this.activeRecordingId = options.recordingId;
        try {
            await this.startRecordingCapture(options);
        }
        catch (error) {
            this.activeRecordingId = undefined;
            throw error;
        }
    }
    async startRecordingCapture(options) {
        if (!this.networkingWorkerProxy || !this.broadcasterPrivateKey) {
            return;
        }
        const upload = readRecordingUploadOptions();
        console.log('[recording]', JSON.stringify({ ev: 'options', ...upload }));
        const { port1, port2 } = new MessageChannel();
        await RecordingCaptureManager.getInstance().start({
            recordingId: options.recordingId,
            streamId: this.streamId,
            broadcasterUserId: toUserId(this.broadcasterUserId),
            visibility: options.visibility,
            broadcasterPrivateKey: this.broadcasterPrivateKey,
            streamKey: this.aesKey,
            backendUrl: options.backendUrl,
            liveUploadBps: upload.liveUploadBps,
            spoolMaxBytes: upload.spoolMaxBytes,
            segmentTargetSeconds: Settings.recordingSegmentTargetSeconds,
            audio: {
                codec: Settings.viewerStreamAudioCodec,
                sampleRate: Settings.audioSampleRate,
                channels: Settings.viewerStreamAudioChannels,
                preSkip: Settings.recordingOpusPreSkip,
                frameDurationUs: Settings.audioFrameDurationMicroseconds,
                silenceFrame: Uint8Array.from(Settings.recordingOpusSilenceFrame),
            },
        }, port1, (state) => {
            // also after this recording has stopped: it is uploaded
            // while the next one may already be recorded
            if (state.status === 'complete') {
                this.recordingStates.delete(state.recordingId);
            }
            else {
                this.recordingStates.set(state.recordingId, state);
            }
            this.emit('recordingStateChanged', state);
        });
        await this.networkingWorkerProxy.startRecordingTap(transfer(port2, [port2]));
        this.captureRecordingThumbnail(options.recordingId);
    }
    /**
     * The picture of the recording in listings: the composite as it is
     * published when the recording starts. Taken from a frame on its way
     * to the encoder, so it costs no decoder, on a phone as little as on a
     * desktop. A recording without one is a recording all the same.
     */
    captureRecordingThumbnail(recordingId) {
        const capture = async () => {
            for (let attempt = 1; attempt <= RECORDING_THUMBNAIL_ATTEMPTS; attempt += 1) {
                if (this.activeRecordingId !== recordingId) {
                    return;
                }
                // no frame comes while the video is off (a narrow uplink)
                const jpeg = await this.mediaManager.captureCompositeFrame(RECORDING_THUMBNAIL_TIMEOUT_MS);
                if (this.activeRecordingId !== recordingId) {
                    return;
                }
                if (jpeg) {
                    await RecordingCaptureManager.getInstance().setThumbnail(recordingId, jpeg);
                    return;
                }
            }
            console.warn('[recording] no picture of the composite, no thumbnail');
        };
        capture().catch((error) => {
            console.warn('[recording] thumbnail failed', error);
        });
    }
    /**
     * Ends capture. Resolves when everything recorded is stored on the
     * device; `recordingStateChanged` reports the upload from there on.
     */
    async stopRecording() {
        if (this.activeRecordingId === undefined) {
            return;
        }
        try {
            await this.networkingWorkerProxy?.stopRecordingTap();
            await RecordingCaptureManager.getInstance().stop();
        }
        finally {
            this.activeRecordingId = undefined;
        }
    }
    isRecording() {
        return this.getRecordingState()?.status === 'recording';
    }
    /** The state of the recording that is being captured. */
    getRecordingState() {
        return this.activeRecordingId === undefined
            ? undefined
            : this.recordingStates.get(this.activeRecordingId);
    }
    /**
     * Finishes the recordings that an earlier page left unfinished (a
     * closed tab, a crash), for the broadcasters whose keys are given.
     * Needs no session.
     *
     * @returns the ids of the recordings that are being finished
     */
    static resumeRecordingUploads(credentials, onState) {
        return RecordingCaptureManager.getInstance().resume(credentials, onState);
    }
    /** Recordings of this page that are not uploaded completely yet. */
    static getUnfinishedRecordingCount() {
        return RecordingCaptureManager.getInstance().getWorkingCount();
    }
    isGapDiagnosticsEnabled() {
        if (!GAP_DIAGNOSTICS_MASTER_ENABLE) {
            return false;
        }
        try {
            return (window.localStorage.getItem('gapDiagnostics') === '1' ||
                new URL(window.location.href).searchParams.get('gapDiagnostics') === 'true');
        }
        catch {
            return false;
        }
    }
    getStreamrWrapper() {
        if (!this.streamrWrapper) {
            throw new Error('Streamr wrapper not initialized');
        }
        return this.streamrWrapper;
    }
    getNetworkingWorker() {
        if (!this.networkingWorkerProxy) {
            throw new Error('NetworkingWorker not initialized');
        }
        return this.networkingWorkerProxy;
    }
    async handleCanvasClick(x, y) {
        await this.mediaManager.handleCanvasClick(x, y);
    }
    clearHeartbeatTimers() {
        if (this.heartbeatTimeoutRef) {
            clearTimeout(this.heartbeatTimeoutRef);
            this.heartbeatTimeoutRef = undefined;
        }
        if (this.heartbeatIntervalRef) {
            clearInterval(this.heartbeatIntervalRef);
            this.heartbeatIntervalRef = undefined;
        }
    }
    // --- Partition helpers (single source of truth) ---
    // Viewer quality: the content partition a viewer/guest watches. The
    // host publishes the composite on the content partition (high) and,
    // on desktop, a low-quality copy on the low content partition; the
    // viewer picks by mode/preference, its own relay capacity (auto
    // decisions arrive through onContentQualityDecision) and what the
    // host announces. Down = break-before-make (leave, then join: instant
    // uplink relief); up = make-before-break (join, flip on the first
    // keyframe, then leave).
    contentPartitionState = { kind: 'idle' };
    contentSwitchEpoch = 0;
    joinedContentPartitions = new Set();
    contentQualityMode = 'auto';
    autoContentQuality = 'high';
    deferredContentQuality;
    lowQualityPartition;
    highQualityAvailable = true;
    viewerQualityTune;
    /** auto decisions and playback health apply (never for a host's own partition) */
    contentQualityAutoAllowed = false;
    /** the partition was chosen by the caller (a host): never reconciled */
    contentPartitionPinned = false;
    contentFailureAttempts = 0;
    contentRetryTimer;
    contentRecovering = false;
    /** URL, connection hint and the service's stored preference/memory → options. */
    static readViewerQualityOptions(stored) {
        return resolveViewerQualityOptions({
            ...readViewerQualityEnvironment(),
            ...stored,
            nowMs: Date.now(),
            memoryTtlMs: Settings.viewerQualityMemoryTtlMs,
        });
    }
    setContentQualityMode(mode, initial, tune) {
        this.contentQualityMode = mode;
        if (initial !== undefined)
            this.autoContentQuality = initial;
        if (tune !== undefined)
            this.viewerQualityTune = tune;
        this.logViewerQuality({ ev: 'mode', mode, initial });
        this.syncContentQualityMonitor();
        this.emitContentQuality();
        void this.reconcileContentPartition('mode');
    }
    syncContentQualityMonitor() {
        this.networkingWorkerProxy
            ?.setContentQualityMonitorEnabled(this.contentQualityAutoAllowed &&
            this.contentQualityMode === 'auto')
            .catch(console.error);
    }
    /**
     * Viewing failed: this connection cannot relay (uplink) or receive
     * (downlink) even the low stream. Leave the content partitions so the
     * neighbors stop suffering, tell the user what is needed, retry later.
     */
    async failContentViewing(reason) {
        const state = this.contentPartitionState;
        if (state.kind === 'idle' || state.kind === 'failed')
            return;
        this.contentFailureAttempts += 1;
        const backoff = Math.min(Settings.viewerQualityFailRetryBackoffMax, 2 ** (this.contentFailureAttempts - 1));
        const retryInMs = Settings.viewerQualityFailRetryMs * backoff;
        const failure = {
            reason,
            minDownlinkMbps: Settings.viewerQualityMinDownlinkMbps,
            minUplinkMbps: Settings.viewerQualityMinUplinkMbps,
            retryAtMs: Date.now() + retryInMs,
            attempt: this.contentFailureAttempts,
        };
        this.logViewerQuality({
            ev: 'fail',
            reason,
            attempt: failure.attempt,
            retryInMs,
        });
        await this.deactivateContentPartition();
        this.contentPartitionState = { kind: 'failed', failure };
        this.contentRetryTimer = setTimeout(() => {
            this.contentRetryTimer = undefined;
            void this.retryContentViewing('auto');
        }, retryInMs);
        this.emitContentQuality();
    }
    /** Retry after a failure (the notice's button, or the automatic timer). */
    async retryContentViewing(source) {
        const state = this.contentPartitionState;
        if (state.kind !== 'failed') {
            this.logViewerQuality({
                ev: 'retry-ignored',
                source,
                state: state.kind,
            });
            return;
        }
        if (this.contentRetryTimer !== undefined) {
            clearTimeout(this.contentRetryTimer);
            this.contentRetryTimer = undefined;
        }
        this.logViewerQuality({
            ev: 'retry',
            source,
            attempt: state.failure.attempt,
        });
        this.contentPartitionState = { kind: 'idle' };
        // start from the low stream again
        this.autoContentQuality = 'low';
        this.contentRecovering = true;
        this.networkingWorkerProxy?.notifyContentRetry().catch(console.error);
        await this.activateContentPartition();
    }
    /** the first content media after a retry clears the notice */
    onContentMediaAfterRetry() {
        if (!this.contentRecovering)
            return;
        this.contentRecovering = false;
        this.logViewerQuality({ ev: 'recovered' });
        this.emitContentQuality();
    }
    /** a relay-capacity or playback decision from the worker / player */
    onContentQualitySignal(quality, reason, origin) {
        if (!this.contentQualityAutoAllowed)
            return;
        const state = this.contentPartitionState;
        if (state.kind !== 'active')
            return;
        if (quality === 'fail') {
            void this.failContentViewing(origin === 'playback' || reason === 'downlink'
                ? 'downlink'
                : 'uplink');
            return;
        }
        if (this.contentQualityMode !== 'auto')
            return;
        this.onContentQualityDecision(quality, reason);
    }
    /** An automatic decision (relay capacity) while in auto mode. */
    onContentQualityDecision(quality, reason) {
        this.autoContentQuality = quality;
        if (this.partitionForQuality(quality) === undefined) {
            // the host has not announced the low partition yet: the switch
            // follows as soon as it does (metadata or the media-borne hint)
            if (this.deferredContentQuality !== quality) {
                this.deferredContentQuality = quality;
                this.logViewerQuality({
                    ev: 'decision-deferred',
                    quality,
                    reason,
                });
            }
            return;
        }
        this.deferredContentQuality = undefined;
        this.logViewerQuality({ ev: 'decision-applied', quality, reason });
        void this.reconcileContentPartition(reason);
    }
    setLowQualityPartition(partition) {
        if (this.lowQualityPartition === partition)
            return;
        this.lowQualityPartition = partition;
        this.logViewerQuality({ ev: 'host', lowQualityPartition: partition });
        this.emitContentQuality();
        void this.reconcileContentPartition('metadata');
    }
    setHighQualityAvailable(available) {
        if (this.highQualityAvailable === available)
            return;
        this.highQualityAvailable = available;
        this.logViewerQuality({ ev: 'host', highQualityAvailable: available });
        this.emitContentQuality();
        void this.reconcileContentPartition(available ? 'high-available' : 'high-suspended');
    }
    /** test hook (Playwright): see NetworkingWorker.setDownlinkDelayTestOffset */
    async setDownlinkDelayTestOffset(offsetMs, partition) {
        await this.networkingWorkerProxy?.setDownlinkDelayTestOffset(offsetMs, partition);
    }
    getContentQualityState() {
        const state = this.contentPartitionState;
        let active;
        if (state.kind === 'active') {
            active = this.qualityOfPartition(state.partition);
        }
        else if (state.kind === 'switching-down' ||
            state.kind === 'switching-up') {
            active = this.qualityOfPartition(state.from);
        }
        let switching;
        if (state.kind === 'switching-down')
            switching = 'down';
        if (state.kind === 'switching-up')
            switching = 'up';
        return {
            mode: this.contentQualityMode,
            active,
            switching,
            lowAvailable: this.lowQualityPartition !== undefined,
            highAvailable: this.highQualityAvailable,
            ...(state.kind === 'failed' ? { failure: state.failure } : {}),
            ...(this.contentRecovering ? { recovering: true } : {}),
        };
    }
    emitContentQuality() {
        this.emit('contentQualityChanged', this.getContentQualityState());
    }
    logViewerQuality(fields) {
        console.log('[viewer-quality]', JSON.stringify(fields));
    }
    qualityOfPartition(partition) {
        return partition === CONTENT_STREAM_PART_PARTITION ? 'high' : 'low';
    }
    partitionForQuality(quality) {
        return quality === 'low'
            ? this.lowQualityPartition
            : CONTENT_STREAM_PART_PARTITION;
    }
    /** the quality to watch given mode, the auto decision and what the host publishes */
    desiredContentQuality() {
        const wanted = this.contentQualityMode === 'auto'
            ? this.autoContentQuality
            : this.contentQualityMode;
        if (this.lowQualityPartition === undefined)
            return 'high';
        if (!this.highQualityAvailable)
            return 'low';
        return wanted;
    }
    /**
     * Join the content partition to watch. Hosts pass their own partition
     * (they publish there); viewers and guests get the one their quality
     * settings call for.
     */
    async activateContentPartition(opts) {
        if (!this.networkingWorkerProxy) {
            return;
        }
        if (this.contentPartitionState.kind !== 'idle') {
            return;
        }
        this.contentQualityAutoAllowed = opts?.partition === undefined;
        this.contentPartitionPinned = opts?.partition !== undefined;
        const partition = opts?.partition ??
            this.partitionForQuality(this.desiredContentQuality()) ??
            CONTENT_STREAM_PART_PARTITION;
        this.contentSwitchEpoch += 1;
        await this.networkingWorkerProxy.setActiveContentPartition(partition);
        await this.networkingWorkerProxy.joinPartition(partition);
        this.joinedContentPartitions.add(partition);
        this.contentPartitionState = { kind: 'active', partition };
        this.logViewerQuality({ ev: 'active', partition, reason: 'join' });
        this.syncContentQualityMonitor();
        this.mediaManager.setPlaybackHealthMonitoring(this.contentQualityAutoAllowed);
        this.networkingWorkerProxy
            .notifyContentSwitchCompleted(this.qualityOfPartition(partition))
            .catch(console.error);
        this.emitContentQuality();
        // the host's announcement or a decision may have arrived while the
        // join was in flight (the reconcile then found no active partition)
        void this.reconcileContentPartition('activated');
    }
    async deactivateContentPartition() {
        const state = this.contentPartitionState;
        if (state.kind === 'switching-up')
            clearTimeout(state.timer);
        if (this.contentRetryTimer !== undefined) {
            clearTimeout(this.contentRetryTimer);
            this.contentRetryTimer = undefined;
        }
        this.contentRecovering = false;
        this.mediaManager.setPlaybackHealthMonitoring(false);
        this.contentSwitchEpoch += 1;
        this.contentPartitionState = { kind: 'idle' };
        await this.networkingWorkerProxy?.setActiveContentPartition(undefined);
        for (const partition of [...this.joinedContentPartitions]) {
            this.joinedContentPartitions.delete(partition);
            await this.networkingWorkerProxy?.leavePartition(partition);
        }
        this.emitContentQuality();
    }
    async reconcileContentPartition(reason) {
        const state = this.contentPartitionState;
        if (state.kind !== 'active' || this.contentPartitionPinned)
            return;
        const target = this.partitionForQuality(this.desiredContentQuality());
        if (target === undefined || target === state.partition)
            return;
        await this.switchContentPartition(target, reason);
    }
    async switchContentPartition(to, reason) {
        const proxy = this.networkingWorkerProxy;
        const state = this.contentPartitionState;
        if (!proxy || state.kind !== 'active' || state.partition === to)
            return;
        const from = state.partition;
        this.contentSwitchEpoch += 1;
        const epoch = this.contentSwitchEpoch;
        const down = this.qualityOfPartition(to) === 'low';
        this.logViewerQuality({
            ev: 'switch',
            direction: down ? 'down' : 'up',
            from,
            to,
            reason,
        });
        if (down) {
            this.contentPartitionState = {
                kind: 'switching-down',
                from,
                to,
                epoch,
            };
            this.emitContentQuality();
            // the old partition's tracks end now: instant uplink relief
            await proxy.setActiveContentPartition(to);
            if (epoch !== this.contentSwitchEpoch)
                return;
            this.joinedContentPartitions.delete(from);
            await proxy.leavePartition(from);
            if (epoch !== this.contentSwitchEpoch)
                return;
            await proxy.joinPartition(to);
            if (epoch !== this.contentSwitchEpoch)
                return;
            this.joinedContentPartitions.add(to);
            this.contentPartitionState = { kind: 'active', partition: to };
            this.logViewerQuality({ ev: 'active', partition: to, reason });
            proxy.notifyContentSwitchCompleted('low').catch(console.error);
            this.emitContentQuality();
            void this.reconcileContentPartition('settled');
            return;
        }
        const timer = setTimeout(() => {
            this.onContentUpgradeTimeout(epoch);
        }, Settings.viewerQualityUpgradeKeyframeTimeoutMs);
        this.contentPartitionState = {
            kind: 'switching-up',
            from,
            to,
            epoch,
            timer,
        };
        this.emitContentQuality();
        await proxy.setPendingContentPartition(to);
        if (epoch !== this.contentSwitchEpoch)
            return;
        await proxy.joinPartition(to);
        if (epoch !== this.contentSwitchEpoch)
            return;
        this.joinedContentPartitions.add(to);
        // completed by onContentPartitionFlipped (first keyframe) or the timeout
    }
    onContentPartitionFlipped(partition) {
        const state = this.contentPartitionState;
        if (state.kind !== 'switching-up' || state.to !== partition)
            return;
        clearTimeout(state.timer);
        this.contentPartitionState = { kind: 'active', partition };
        this.logViewerQuality({ ev: 'active', partition, reason: 'flip' });
        this.networkingWorkerProxy
            ?.notifyContentSwitchCompleted(this.qualityOfPartition(partition))
            .catch(console.error);
        this.emitContentQuality();
        this.joinedContentPartitions.delete(state.from);
        this.networkingWorkerProxy
            ?.leavePartition(state.from)
            .catch(console.error);
        void this.reconcileContentPartition('settled');
    }
    onContentUpgradeTimeout(epoch) {
        const state = this.contentPartitionState;
        if (state.kind !== 'switching-up' || state.epoch !== epoch)
            return;
        this.logViewerQuality({
            ev: 'upgrade-timeout',
            from: state.from,
            to: state.to,
        });
        this.abandonContentUpgrade();
    }
    /** the pending partition arrives late: abandon the probe before it flips */
    onContentUpgradeAborted(reason, delayMs) {
        const state = this.contentPartitionState;
        if (state.kind !== 'switching-up')
            return;
        this.logViewerQuality({
            ev: 'upgrade-aborted',
            from: state.from,
            to: state.to,
            reason,
            delayMs: Math.round(delayMs),
        });
        this.abandonContentUpgrade();
    }
    /** back to the partition being played; the joined one is left */
    abandonContentUpgrade() {
        const state = this.contentPartitionState;
        if (state.kind !== 'switching-up')
            return;
        clearTimeout(state.timer);
        this.contentPartitionState = { kind: 'active', partition: state.from };
        this.joinedContentPartitions.delete(state.to);
        const proxy = this.networkingWorkerProxy;
        void (async () => {
            await proxy?.setPendingContentPartition(undefined);
            await proxy?.leavePartition(state.to);
            await proxy?.notifyContentUpgradeFailed();
        })().catch(console.error);
        this.emitContentQuality();
    }
    /**
     * The low content partition carries the host's low-quality composite;
     * a broadcaster joins it before publishing so its entrypoint is
     * registered by the first keyframe.
     */
    async activateLowContentPartition() {
        if (!this.networkingWorkerProxy || this.lowContentPartitionJoined) {
            return;
        }
        this.lowContentPartitionJoined = true;
        await this.networkingWorkerProxy.joinPartition(LOW_CONTENT_STREAM_PART_PARTITION);
    }
    async deactivateLowContentPartition() {
        if (!this.lowContentPartitionJoined)
            return;
        this.lowContentPartitionJoined = false;
        await this.networkingWorkerProxy?.leavePartition(LOW_CONTENT_STREAM_PART_PARTITION);
    }
    /** Whether this desktop broadcaster also publishes the low-quality composite. */
    getLowQualityStreamOptions() {
        return readLowQualityStreamOptions();
    }
    async activateSpeakerPartition() {
        if (!this.networkingWorkerProxy) {
            return;
        }
        await this.networkingWorkerProxy.joinPartition(SPEAKER_STREAM_PART_PARTITION);
    }
    async deactivateSpeakerPartition() {
        await this.networkingWorkerProxy?.leavePartition(SPEAKER_STREAM_PART_PARTITION);
    }
    syncSpeakerPartitionPublishing() {
        this.networkingWorkerProxy?.setSpeakerPartitionPublishingEnabled(this.roleUsesSpeakerPartition(this.userRole));
    }
    // --- Partition role helpers ---
    roleUsesContentPartition(role) {
        return role === UserRole.VIEWER || role === UserRole.GUEST;
    }
    roleUsesSpeakerPartition(role) {
        if (role === UserRole.SPEAKER)
            return true;
        if (role === UserRole.COHOST && this.acceptedParticipantType === 1) {
            return true;
        }
        return false;
    }
    shouldSkipIndividualPorts() {
        return (this.userRole === UserRole.COHOST &&
            this.acceptedParticipantType !== 1);
    }
    async startWorkersForRole(role) {
        const promises = [];
        if (this.roleNeedsPlayerWorker(role)) {
            promises.push(this.mediaManager.startVideoPlayerWorker());
        }
        if (this.roleNeedsDecoderWorker(role)) {
            promises.push(this.mediaManager.startVideoDecoderWorker());
        }
        if (this.roleNeedsEncoderWorkers(role)) {
            promises.push(this.mediaManager.startEncoderWorkers());
        }
        await Promise.all(promises);
    }
    roleNeedsPlayerWorker(role) {
        return role !== UserRole.BANNED;
    }
    roleNeedsDecoderWorker(role) {
        return role !== UserRole.BANNED && role !== UserRole.CAMERA;
    }
    roleNeedsEncoderWorkers(role) {
        return (role === UserRole.VIEWER ||
            role === UserRole.SPEAKER ||
            role === UserRole.COHOST ||
            role === UserRole.CAMERA ||
            role === UserRole.GUEST);
    }
    // --- Public role API ---
    async setAcceptedParticipantType(type) {
        const oldUseSpeaker = this.roleUsesSpeakerPartition(this.userRole);
        const oldSkipIndividual = this.shouldSkipIndividualPorts();
        this.acceptedParticipantType = type;
        const newUseSpeaker = this.roleUsesSpeakerPartition(this.userRole);
        const newSkipIndividual = this.shouldSkipIndividualPorts();
        if (oldUseSpeaker && !newUseSpeaker) {
            await this.deactivateSpeakerPartition();
        }
        else if (!oldUseSpeaker && newUseSpeaker) {
            await this.activateSpeakerPartition();
        }
        this.syncSpeakerPartitionPublishing();
        if (oldSkipIndividual && !newSkipIndividual) {
            await this.mediaManager.wireNetworkingPorts({
                skipIndividualPorts: false,
            });
        }
        if (oldSkipIndividual !== newSkipIndividual) {
            this.mediaManager.setPerTrackAudioEncodingEnabled(!newSkipIndividual);
            this.mediaManager.setPerTrackVideoEncodingEnabled(!newSkipIndividual);
        }
    }
    getBroadcasterUserId() {
        return toUserId(this.broadcasterUserId);
    }
    /**
     * Low-bandwidth ladder: may the viewer composite's video be turned off.
     * Not while a recorder consumes the stream (it must not see a `done`
     * followed by a same-id resume).
     */
    setCompositeVideoOffAllowed(allowed) {
        this.networkingWorkerProxy
            ?.setLowBwVideoOffAllowed('composite', allowed)
            .catch(console.error);
    }
    getUserRole() {
        return this.userRole;
    }
    getAcceptedParticipantType() {
        return this.acceptedParticipantType;
    }
    /**
     * The content partition follows the role: on for viewers and guests, off
     * for the rest. Returns true when it changed something.
     *
     * A promotion between two roles that both watch the composite must still
     * activate it when it never was: a viewer accepted as a guest before it
     * started watching (the auto-join link asks for guest access as soon as
     * the host's metadata arrives, ahead of the lobby's dismissal that
     * normally activates the partition) stayed without the host's video for
     * the whole session (live session 2026-09-21).
     */
    async syncContentPartitionForRole(oldRole, newRole) {
        const oldContent = this.roleUsesContentPartition(oldRole);
        const newContent = this.roleUsesContentPartition(newRole);
        if (oldContent && !newContent) {
            await this.deactivateContentPartition();
            return true;
        }
        if (newContent && this.contentPartitionState.kind === 'idle') {
            await this.activateContentPartition();
            return true;
        }
        return false;
    }
    async setUserRole(newRole) {
        const oldRole = this.userRole;
        if (oldRole === newRole) {
            return;
        }
        console.log(`StreamrAppApi::setUserRole() ${oldRole} -> ${newRole}`);
        this.userRole = newRole;
        const oldSpeaker = this.roleUsesSpeakerPartition(oldRole);
        const newSpeaker = this.roleUsesSpeakerPartition(newRole);
        const oldNeedsEncoders = this.roleNeedsEncoderWorkers(oldRole);
        const newNeedsEncoders = this.roleNeedsEncoderWorkers(newRole);
        const oldNeedsDecoder = this.roleNeedsDecoderWorker(oldRole);
        const newNeedsDecoder = this.roleNeedsDecoderWorker(newRole);
        const oldNeedsPlayer = this.roleNeedsPlayerWorker(oldRole);
        const newNeedsPlayer = this.roleNeedsPlayerWorker(newRole);
        // Start workers that the new role needs (before joining partitions).
        // Yield between each heavy step so the main thread services
        // MessagePort events (notably the AudioWorklet `played` callback)
        // and we don't accumulate a multi-second microtask burst.
        if (!oldNeedsPlayer && newNeedsPlayer) {
            await this.mediaManager.startVideoPlayerWorker();
            await yieldToEventLoop();
        }
        if (!oldNeedsDecoder && newNeedsDecoder) {
            await this.mediaManager.startVideoDecoderWorker();
            await yieldToEventLoop();
        }
        if (!oldNeedsEncoders && newNeedsEncoders) {
            await this.mediaManager.startEncoderWorkers();
            await yieldToEventLoop();
        }
        const anyWorkerStarted = (!oldNeedsPlayer && newNeedsPlayer) ||
            (!oldNeedsDecoder && newNeedsDecoder) ||
            (!oldNeedsEncoders && newNeedsEncoders);
        if (anyWorkerStarted) {
            await this.mediaManager.wireNetworkingPorts({
                skipIndividualPorts: this.shouldSkipIndividualPorts(),
            });
            await yieldToEventLoop();
        }
        this.mediaManager.setPerTrackAudioEncodingEnabled(!this.shouldSkipIndividualPorts());
        this.mediaManager.setPerTrackVideoEncodingEnabled(!this.shouldSkipIndividualPorts());
        if (await this.syncContentPartitionForRole(oldRole, newRole)) {
            await yieldToEventLoop();
        }
        if (oldSpeaker && !newSpeaker) {
            await this.deactivateSpeakerPartition();
            await yieldToEventLoop();
        }
        else if (!oldSpeaker && newSpeaker) {
            await this.activateSpeakerPartition();
            await yieldToEventLoop();
        }
        this.syncSpeakerPartitionPublishing();
        if (oldNeedsEncoders && !newNeedsEncoders) {
            await this.mediaManager.stopEncoderWorkers();
            await yieldToEventLoop();
        }
        if (oldNeedsDecoder && !newNeedsDecoder) {
            await this.mediaManager.stopVideoDecoderWorker();
            await yieldToEventLoop();
        }
        if (oldNeedsPlayer && !newNeedsPlayer) {
            await this.mediaManager.stopVideoPlayerWorker();
        }
        this.setHeartbeatInterval();
    }
    // --- One-to-one stream methods ---
    async joinOneToOneReceiver(userId, streamUrl) {
        const canonical = canonicalUserId(userId);
        const { streamId, aesKey } = parseOneToOneStreamUrl(streamUrl);
        const previousStreamId = this.oneToOneStreamsByUser.get(canonical);
        this.oneToOneStreamsByUser.set(canonical, streamId);
        await this.networkingWorkerProxy?.joinOneToOneStream(streamId, aesKey);
        if (previousStreamId !== undefined && previousStreamId !== streamId) {
            console.log(`[StreamrAppApi] one-to-one stream rotated for ${canonical.slice(0, 12)} — leaving previous stream`);
            try {
                await this.networkingWorkerProxy?.leaveOneToOneStream(previousStreamId);
            }
            catch {
                // best effort
            }
        }
        this.watchOneToOneReceiver(canonical, streamId, aesKey);
        this.requestN1StartForGuest(canonical, 'one-to-one receiver joined');
    }
    /**
     * Re-roll a one-to-one stream that carries no media, by the rules of
     * OneToOneRejoinPolicy: once a second the part's neighbors and the age of
     * the last inbound packet are read from the networking worker, and the
     * stream is left and joined again only when the part has not formed for
     * a long time or has formed and stays silent. A blind timer here used to
     * fire before the part had formed and tear down the forming link, from
     * both sides at once. `current` says whether this watch still owns the
     * stream; `mediaFlowing` is this side's own notion of arrived media (the
     * inbound packets seen by the worker count as well: audio the page
     * cannot play yet is dropped after that point, which is no topology
     * fault).
     */
    watchOneToOne(label, streamId, aesKey, tunables, current, mediaFlowing, rejoinLog) {
        const policy = new OneToOneRejoinPolicy(tunables, Date.now());
        const joinedAtMs = Date.now();
        let formedLogged = false;
        const tick = async () => {
            if (!current())
                return;
            const state = (await this.networkingWorkerProxy?.getOneToOneState(streamId)) ?? { neighborCount: 0 };
            if (!current())
                return;
            const packetsArriving = state.inboundAgeMs !== undefined &&
                state.inboundAgeMs < ONE_TO_ONE_INBOUND_FRESH_MS;
            const decision = policy.evaluate({
                nowMs: Date.now(),
                neighborCount: state.neighborCount,
                mediaFlowing: mediaFlowing() || packetsArriving,
            });
            if (decision.type === 'done')
                return;
            if (decision.type === 'give-up') {
                console.warn(`[StreamrAppApi] ${label}: no media after ${policy.getRejoinCount()} re-joins — leaving the one-to-one stream as it is`);
                return;
            }
            if (decision.type === 'wait' && decision.formed && !formedLogged) {
                formedLogged = true;
                console.log(`[StreamrAppApi] ${label}: one-to-one part formed ${Date.now() - joinedAtMs}ms after join, waiting for media`);
            }
            if (decision.type === 'rejoin') {
                console.log(rejoinLog(decision.reason, decision.waitedMs, decision.attempt));
                try {
                    await this.networkingWorkerProxy?.leaveOneToOneStream(streamId);
                    await this.networkingWorkerProxy?.joinOneToOneStream(streamId, aesKey);
                }
                catch {
                    // ignore rejoin errors; the next tick covers it
                }
                policy.rejoined(Date.now());
                formedLogged = false;
            }
            setTimeout(() => {
                tick().catch(console.error);
            }, ONE_TO_ONE_WATCH_TICK_MS);
        };
        setTimeout(() => {
            tick().catch(console.error);
        }, ONE_TO_ONE_WATCH_TICK_MS);
    }
    /**
     * Host side: the guest's media must arrive on its one-to-one stream. The
     * guest re-rolls a silent stream first (its join direction forms
     * reliably); this is the slow fallback.
     */
    watchOneToOneReceiver(userId, streamId, aesKey) {
        const uid = canonicalUserId(userId);
        const short = uid.slice(0, 12);
        this.watchOneToOne(`receiver for ${short}`, streamId, aesKey, HOST_ONE_TO_ONE_REJOIN, () => this.oneToOneStreamsByUser.get(uid) === streamId, () => this.mediaManager
            .getActivePublishers()
            .some((p) => canonicalUserId(p) === uid), (reason, waitedMs, attempt) => `[StreamrAppApi] No media from ${short}: one-to-one ${reason} for ${waitedMs}ms, re-joining one-to-one stream (attempt ${attempt}/${HOST_ONE_TO_ONE_REJOIN.maxRejoins})`);
    }
    /**
     * @param options.existingPart the host already sits on this stream's
     * part (a reload restoring the guest's role): its formation is quick
     * when it works, so a silent part is re-rolled early.
     */
    async joinOneToOnePublisher(streamUrl, options = {}) {
        const { streamId, aesKey } = parseOneToOneStreamUrl(streamUrl);
        const previousStreamId = this.ownOneToOneStreamId;
        if (previousStreamId !== streamId) {
            this.oneToOneInboundAudioSeen = false;
        }
        this.ownOneToOneStreamId = streamId;
        this.networkingWorkerProxy?.setOneToOneStreamId(streamId);
        await this.networkingWorkerProxy?.joinOneToOneStream(streamId, aesKey);
        if (previousStreamId !== undefined && previousStreamId !== streamId) {
            console.log('[StreamrAppApi] own one-to-one stream rotated — leaving previous stream');
            try {
                await this.networkingWorkerProxy?.leaveOneToOneStream(previousStreamId);
            }
            catch {
                // best effort
            }
        }
        this.ownOneToOneRejoinEpoch++;
        this.ownOneToOneJoinedAtMs = Date.now();
        this.watchOwnOneToOne(streamId, aesKey, this.ownOneToOneRejoinEpoch, options.existingPart
            ? GUEST_EXISTING_PART_REJOIN
            : GUEST_ONE_TO_ONE_REJOIN);
    }
    /**
     * Guest side: the host's N-1 mix must arrive on the own one-to-one
     * stream. The host only starts that mix once the guest's audio has
     * reached it, so the silence after a join legitimately lasts the part's
     * formation plus a round trip; the policy waits that out and re-rolls
     * only a part that does not form or stays silent once formed. A newer
     * joinOneToOnePublisher call owns the watch from then on (epoch).
     */
    watchOwnOneToOne(streamId, aesKey, epoch, tunables) {
        this.watchOneToOne('own stream', streamId, aesKey, tunables, () => this.ownOneToOneRejoinEpoch === epoch &&
            this.ownOneToOneStreamId === streamId, () => this.oneToOneInboundAudioSeen, (reason, waitedMs, attempt) => `[StreamrAppApi] no inbound one-to-one audio: own one-to-one ${reason} for ${waitedMs}ms — re-joining own stream (attempt ${attempt}/${tunables.maxRejoins})`);
    }
    async leaveOneToOneStream(userId) {
        const canonical = canonicalUserId(userId);
        const streamId = this.oneToOneStreamsByUser.get(canonical);
        if (streamId) {
            this.oneToOneStreamsByUser.delete(canonical);
            this.clearPendingN1ForGuest(canonical);
            await this.networkingWorkerProxy?.leaveOneToOneStream(streamId);
        }
    }
    async leaveOwnOneToOneStream() {
        if (this.ownOneToOneStreamId) {
            const streamId = this.ownOneToOneStreamId;
            this.ownOneToOneStreamId = undefined;
            this.networkingWorkerProxy?.setOneToOneStreamId(undefined);
            await this.networkingWorkerProxy?.leaveOneToOneStream(streamId);
        }
    }
    // --- N-1 audio for guests ---
    async stopContentAudio() {
        await this.mediaManager.stopContentAudio();
    }
    async resumeContentAudio() {
        await this.mediaManager.resumeContentAudio();
    }
    waitForContentAudioReady(timeoutMs) {
        return new Promise((resolve) => {
            const onReady = () => {
                clearTimeout(timer);
                resolve(true);
            };
            const timer = setTimeout(() => {
                this.mediaManager.off('contentAudioReady', onReady);
                console.warn(`[StreamrAppApi] waitForContentAudioReady timed out after ${timeoutMs}ms`);
                resolve(false);
            }, timeoutMs);
            this.mediaManager.once('contentAudioReady', onReady);
        });
    }
    /** `timeoutMs <= 0` waits until the N-1 audio plays, however long that takes. */
    waitForOneToOneAudioReady(timeoutMs) {
        return new Promise((resolve) => {
            const onReady = () => {
                if (timer !== undefined)
                    clearTimeout(timer);
                resolve(true);
            };
            const timer = timeoutMs > 0
                ? setTimeout(() => {
                    this.mediaManager.off('oneToOneAudioReady', onReady);
                    console.warn(`[StreamrAppApi] waitForOneToOneAudioReady timed out after ${timeoutMs}ms`);
                    resolve(false);
                }, timeoutMs)
                : undefined;
            this.mediaManager.once('oneToOneAudioReady', onReady);
        });
    }
    requestN1StartForGuest(userId, reason) {
        const canonical = canonicalUserId(userId);
        this.pendingN1GuestIds.add(canonical);
        this.tryFulfillPendingN1ForGuest(canonical, reason).catch(console.error);
    }
    async startN1AudioForGuest(guestUserId) {
        const canonical = canonicalUserId(guestUserId);
        this.pendingN1GuestIds.add(canonical);
        await this.tryFulfillPendingN1ForGuest(canonical, 'explicit request');
    }
    async tryFulfillPendingN1ForGuest(guestUserId, reason) {
        const canonical = canonicalUserId(guestUserId);
        if (!this.pendingN1GuestIds.has(canonical))
            return;
        if (this.n1StartInFlight.has(canonical))
            return;
        const streamId = this.oneToOneStreamsByUser.get(canonical);
        if (!streamId) {
            this.logPendingN1Reason(canonical, 'waiting for one-to-one stream', reason);
            return;
        }
        if (!this.userManager) {
            this.logPendingN1Reason(canonical, 'waiting for user manager', reason);
            return;
        }
        const user = this.userManager.getUser(canonical);
        if (!user) {
            this.logPendingN1Reason(canonical, 'waiting for user', reason);
            return;
        }
        if (user.status !== UserRole.GUEST) {
            this.clearPendingN1ForGuest(canonical);
            return;
        }
        if (this.mediaManager.getAudioTrackIdsForUser(canonical).length === 0) {
            this.logPendingN1Reason(canonical, 'waiting for inbound audio track', reason);
            return;
        }
        this.n1StartInFlight.add(canonical);
        this.n1PendingReasons.delete(canonical);
        try {
            await this.mediaManager.startN1PublishingForGuest(canonical, streamId);
            this.pendingN1GuestIds.delete(canonical);
        }
        finally {
            this.n1StartInFlight.delete(canonical);
        }
    }
    logPendingN1Reason(guestUserId, pendingReason, triggerReason) {
        if (this.n1PendingReasons.get(guestUserId) === pendingReason)
            return;
        this.n1PendingReasons.set(guestUserId, pendingReason);
        console.log(`[StreamrAppApi] N-1 pending for ${guestUserId}: ${pendingReason} (${triggerReason})`);
    }
    clearPendingN1ForGuest(guestUserId) {
        const canonical = canonicalUserId(guestUserId);
        this.pendingN1GuestIds.delete(canonical);
        this.n1PendingReasons.delete(canonical);
        this.n1StartInFlight.delete(canonical);
    }
    clearAllPendingN1() {
        this.pendingN1GuestIds.clear();
        this.n1PendingReasons.clear();
        this.n1StartInFlight.clear();
    }
    clearPendingN1ForSession(userSessionId) {
        const userId = userSessionId.split(':')[0];
        if (userId) {
            this.clearPendingN1ForGuest(canonicalUserId(userId));
        }
    }
    async stopN1AudioForGuest(guestUserId) {
        this.clearPendingN1ForGuest(guestUserId);
        await this.mediaManager.stopN1PublishingForGuest(canonicalUserId(guestUserId));
    }
    async leaveAllOneToOneStreams() {
        const userIds = Array.from(this.oneToOneStreamsByUser.keys());
        for (const userId of userIds) {
            await this.leaveOneToOneStream(userId).catch(() => undefined);
        }
        await this.leaveOwnOneToOneStream().catch(() => undefined);
    }
    isBroadcaster() {
        return (this.ownUserId != null &&
            areEqualBinaries(this.ownUserId, this.broadcasterUserId));
    }
    publishBroadcastMetadata(broadcastMetadata) {
        this.networkingWorkerProxy?.publishBroadcastMetadata(broadcastMetadata);
    }
    async setContentPublishingEnabled(enabled) {
        this.networkingWorkerProxy?.setContentPublishingEnabled(enabled);
        if (enabled) {
            // the host publishes on the content partition itself
            await this.activateContentPartition({
                partition: CONTENT_STREAM_PART_PARTITION,
            });
        }
        else {
            await this.deactivateContentPartition();
        }
    }
    // --- NetworkingWorker event handler ---
    onNetworkingWorkerEvent = (event) => {
        if ('publisherId' in event) {
            // somebody else is on the control partition with us
            this.peerDiagManager?.setReachable(true);
        }
        switch (event.type) {
            case 'streamingStateChanged':
                this.emit('streamingStateChanged', event.streaming);
                break;
            case 'broadcastMetadata':
                this.emit('broadcastMetadataChanged', event.broadcastMetadata);
                break;
            case 'contentPartitionFlipped':
                this.onContentPartitionFlipped(event.partition);
                break;
            case 'contentUpgradeAborted':
                this.onContentUpgradeAborted(event.reason, event.delayMs);
                break;
            case 'contentMediaAfterRetry':
                this.onContentMediaAfterRetry();
                break;
            case 'contentQualityHint':
                // the broadcaster's announcement carried by the media itself
                if (event.lowQualityPartition !== undefined) {
                    this.setLowQualityPartition(event.lowQualityPartition);
                }
                if (event.highQualityAvailable !== undefined) {
                    this.setHighQualityAvailable(event.highQualityAvailable);
                }
                break;
            case 'contentQualityDecision':
                this.onContentQualitySignal(event.quality, event.reason, 'relay');
                break;
            case 'compositeSuspendRequested':
                if (event.kind !== 'composite')
                    break;
                this.mediaManager
                    .setViewerCompositeSuspended(event.suspend)
                    .then(() => {
                    this.emit('compositeSuspendChanged', event.suspend);
                })
                    .catch(console.error);
                break;
            case 'pluginMessage':
                if (event.pluginMessage.pluginId === PEER_DIAG_PLUGIN_ID) {
                    // diagnostics transport, not a plugin's message
                    this.peerDiagManager?.onNetworkMessage(event.pluginMessage.data);
                    this.peerDiagSink?.onNetworkMessage(String(toUserId(event.publisherId)), event.pluginMessage.data);
                    break;
                }
                this.emit('pluginMessage', event.pluginMessage, event.publisherId);
                this.streamrWrapper?.emit('pluginMessage', event.pluginMessage, event.publisherId);
                break;
            case 'purchaseConfirmedMessage':
                if (!areEqualBinaries(event.publisherId, this.broadcasterUserId)) {
                    return;
                }
                this.emit('purchaseConfirmedMessage', event.purchaseConfirmedMessage, event.publisherId);
                break;
            case 'heartbeat':
                this.onHeartbeat(event.heartbeat, event.publisherId);
                break;
            case 'leave':
                this.clearPendingN1ForGuest(event.userId);
                this.mediaManager.handleNetworkLeave(event.userId, event.messageChainId);
                break;
            case 'permissionRequest':
                this.streamrWrapper?.emit('permissionRequest', event.permissionRequest, event.publisherId);
                break;
            case 'setUserRole':
                this.streamrWrapper?.emit('setUserRole', event.setUserRole, event.publisherId);
                break;
            case 'privateMessage':
                this.streamrWrapper?.emit('privateMessage', event.privateMessage, event.publisherId);
                break;
            case 'recorderInstruction':
                this.streamrWrapper?.emit('recorderInstruction', event.recorderInstruction, event.publisherId);
                break;
            case 'purchaseClaimedMessage':
                this.streamrWrapper?.emit('purchaseClaimedMessage', event.purchaseClaimedMessage, event.publisherId);
                break;
            case 'newVideoTrack':
                this.onContentMediaAfterRetry();
                this.mediaManager.handleNewInboundVideoTrack(event.trackId, event.userSessionId, event.userId);
                break;
            case 'videoTrackDone':
                this.mediaManager.handleInboundVideoTrackDone(event.trackId);
                break;
            case 'newAudioTrack':
                if (!event.isOneToOne)
                    this.onContentMediaAfterRetry();
                if (event.isOneToOne) {
                    if (!this.oneToOneInboundAudioSeen) {
                        console.log(`[StreamrAppApi] inbound one-to-one audio arrived${this.ownOneToOneJoinedAtMs !== undefined
                            ? ` ${Date.now() - this.ownOneToOneJoinedAtMs}ms after publisher join`
                            : ''}`);
                    }
                    this.oneToOneInboundAudioSeen = true;
                    this.mediaManager.exemptTrackFromContentStop(event.trackId);
                }
                this.mediaManager.handleNewInboundAudioTrack(event.trackId, event.userSessionId, event.userId);
                this.requestN1StartForGuest(event.userId, 'inbound audio track discovered');
                break;
            case 'audioTrackDone':
                this.mediaManager.handleInboundAudioTrackDone(event.trackId);
                break;
            case 'staleSessionCleaned':
                this.mediaManager.handleStaleSessionCleaned(event.userSessionId, event.staleTracks);
                this.clearPendingN1ForSession(event.userSessionId);
                break;
            case 'timekeeperUpdate':
                // Handled in-worker via shared Timekeeper SAB. Retained as a
                // no-op until the producer side stops emitting (event type is
                // removed once consumers are migrated).
                break;
            case 'remoteSpeechState':
                this.mediaManager.handleRemoteSpeechState(event.userSessionId, event.userId, event.audioLevel, event.isSpeaking);
                break;
        }
    };
    sendLeave = () => {
        this.networkingWorkerProxy?.publishLeave();
    };
    setDisplayName(name) {
        this.displayName = name;
    }
    setUserManager(userManager) {
        this.userManager = userManager;
        if (this.lastLocalConnectionSummary !== undefined) {
            userManager.setLocalConnectionSummary(this.lastLocalConnectionSummary);
        }
        userManager.setIsUserMediaFlowing((userId) => {
            const uid = canonicalUserId(userId);
            return this.mediaManager
                .getActivePublishers()
                .some((p) => canonicalUserId(p) === uid);
        });
        for (const guestUserId of this.pendingN1GuestIds) {
            this.tryFulfillPendingN1ForGuest(guestUserId, 'user manager available').catch(console.error);
        }
    }
    sendHeartbeat = () => {
        this.networkingWorkerProxy
            ?.publishHeartbeat(false, this.displayName, this.userManager?.getSessionNodeIds() ?? [])
            .then((summary) => {
            // handleHeartbeat ignores our own userId, so the local user's
            // People-row badge is fed from the summary we just published
            if (summary !== undefined) {
                this.lastLocalConnectionSummary = summary;
                this.userManager?.setLocalConnectionSummary(summary);
            }
        })
            .catch(() => { });
    };
    publishHeartbeatNow() {
        this.sendHeartbeat();
    }
    onBeforeUnload = () => {
        this.sendLeave();
        // Best-effort: tear down the VideoWorker so its decoders are closed and
        // the iOS hardware decode sessions are released before the page goes
        // away. On a hard navigation / tab close React's unmount (and thus
        // stop()) does NOT run, so without this each load leaks a hardware
        // session; ~a dozen accumulate and exhaust the device's hardware codec
        // (every subsequent isConfigSupported then hangs → black screen).
        // beforeunload — unlike pagehide/visibilitychange — fires on
        // navigation/close but NOT on tab switch, so a backgrounded-then-
        // resumed tab is left intact. Fire-and-forget: beforeunload cannot
        // await, but the close message reaches the worker before it is killed.
        this.mediaManager.stopVideoPlayerWorker().catch(() => { });
    };
    getHeartbeatInterval() {
        const isElevated = this.userRole === UserRole.SPEAKER ||
            this.userRole === UserRole.COHOST ||
            this.userRole === UserRole.GUEST ||
            this.userRole === UserRole.CAMERA;
        return isElevated
            ? Settings.speakerHeartbeatInterval
            : Settings.defaultHeartbeatInterval;
    }
    setupHeartbeat() {
        window.addEventListener('beforeunload', this.onBeforeUnload);
        this.sendHeartbeat();
        const interval = this.getHeartbeatInterval();
        const randomDelay = Math.floor(Math.random() * interval);
        this.heartbeatTimeoutRef = setTimeout(() => {
            this.heartbeatTimeoutRef = undefined;
            this.sendHeartbeat();
            this.clearHeartbeatTimers();
            this.heartbeatIntervalRef = setInterval(() => {
                this.sendHeartbeat();
            }, interval);
        }, randomDelay);
    }
    setHeartbeatInterval() {
        this.clearHeartbeatTimers();
        const interval = this.getHeartbeatInterval();
        this.sendHeartbeat();
        this.heartbeatIntervalRef = setInterval(() => {
            this.sendHeartbeat();
        }, interval);
    }
    onHeartbeat = (heartbeat, publisherId) => {
        const userId = toUserId(publisherId);
        this.userManager?.handleHeartbeat(userId, heartbeat);
        if (heartbeat.displayName !== undefined &&
            heartbeat.displayName.length > 0) {
            this.userManager?.updateRemoteDisplayName(userId, heartbeat.displayName);
        }
    };
    // ========== Worker creation ==========
    createNetworkingWorkerInstance() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./streamrapp/NetworkingWorker.ts', import.meta.url), { type: 'module', name: perfWorkerName('networkingWorker') });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./streamrapp/NetworkingWorker${ext}`, import.meta.url, perfWorkerName('networkingWorker'));
    }
}
